import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PageContainer from '../components/layout/PageContainer';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import {
  Shield, Clock, CheckCircle, XCircle, Users, FileText, Map,
  Eye, Search, AlertTriangle, Award,
} from 'lucide-react';
import {
  getAdminStats, getPendingAtlasFiles, getPendingItineraries,
  getAllAtlasFiles, getAllItineraries, approveAtlasFile, rejectAtlasFile,
  unpublishAtlasFile, approveItinerary, rejectItinerary, unpublishItinerary,
  getUsers, updateUserRole, resetServerReady,
} from '../api/admin';

const STATUS_BADGES = {
  approved: { bg: 'bg-green-50', text: 'text-green-700', label: 'Approved' },
  pending: { bg: 'bg-amber-50', text: 'text-amber-700', label: 'Pending' },
  rejected: { bg: 'bg-red-50', text: 'text-red-700', label: 'Rejected' },
  draft: { bg: 'bg-platinum-100', text: 'text-platinum-600', label: 'Draft' },
};

function StatusBadge({ status }) {
  const config = STATUS_BADGES[status] || STATUS_BADGES.draft;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      {status === 'pending' && <Clock size={10} />}
      {status === 'approved' && <CheckCircle size={10} />}
      {status === 'rejected' && <XCircle size={10} />}
      {config.label}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Card className="flex items-center gap-4">
      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${color}`}>
        <Icon size={22} />
      </div>
      <div>
        <p className="text-2xl font-semibold text-charcoal-500">{value}</p>
        <p className="text-sm text-platinum-600">{label}</p>
      </div>
    </Card>
  );
}

function AdminDashboardPage() {
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('pending');
  const [stats, setStats] = useState(null);
  const [pendingAtlas, setPendingAtlas] = useState([]);
  const [pendingItins, setPendingItins] = useState([]);
  const [allAtlas, setAllAtlas] = useState([]);
  const [allItineraries, setAllItineraries] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersError, setUsersError] = useState(null);
  const [contentError, setContentError] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/');
    }
  }, [authLoading, isAdmin, navigate]);

  // Pre-warm the Render backend on mount so it's awake by the time auth resolves
  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://travelatlas.onrender.com';
    fetch(`${apiUrl}/health`, { mode: 'no-cors' }).catch(() => {});
  }, []);

  useEffect(() => {
    if (isAdmin) fetchData();
  }, [isAdmin]);

  const fetchData = async () => {
    setLoading(true);
    setStatsError(null);
    try {
      const [statsData, pendingA, pendingI] = await Promise.all([
        getAdminStats(),
        getPendingAtlasFiles(),
        getPendingItineraries(),
      ]);
      setStats(statsData);
      setPendingAtlas(pendingA || []);
      setPendingItins(pendingI || []);
    } catch (err) {
      console.error('Error fetching admin data:', err);
      setStatsError(err.message || 'Failed to load dashboard data');
    }
    setLoading(false);
  };

  const fetchAllContent = async () => {
    setContentLoading(true);
    setContentError(null);
    try {
      const [atlasData, itinData] = await Promise.all([
        getAllAtlasFiles(),
        getAllItineraries(),
      ]);
      setAllAtlas(atlasData || []);
      setAllItineraries(itinData || []);
    } catch (err) {
      console.error('Error fetching all content:', err);
      setContentError(err.message || 'Failed to load content');
    }
    setContentLoading(false);
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const data = await getUsers();
      setUsers(data || []);
    } catch (err) {
      console.error('Error fetching users:', err);
      setUsersError(err.message || 'Failed to load users');
    }
    setUsersLoading(false);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'content' && allAtlas.length === 0 && allItineraries.length === 0) fetchAllContent();
    if (tab === 'users' && users.length === 0) fetchUsers();
  };

  const handleApproveAtlas = async (id) => {
    await approveAtlasFile(id);
    setPendingAtlas(prev => prev.filter(f => f.id !== id));
    setStats(prev => prev ? { ...prev, pending_atlas: prev.pending_atlas - 1, total_pending: prev.total_pending - 1 } : prev);
  };

  const handleRejectAtlas = async (id) => {
    await rejectAtlasFile(id);
    setPendingAtlas(prev => prev.filter(f => f.id !== id));
    setStats(prev => prev ? { ...prev, pending_atlas: prev.pending_atlas - 1, total_pending: prev.total_pending - 1 } : prev);
  };

  const handleApproveItin = async (id) => {
    await approveItinerary(id);
    setPendingItins(prev => prev.filter(f => f.id !== id));
    setStats(prev => prev ? { ...prev, pending_itineraries: prev.pending_itineraries - 1, total_pending: prev.total_pending - 1 } : prev);
  };

  const handleRejectItin = async (id) => {
    await rejectItinerary(id);
    setPendingItins(prev => prev.filter(f => f.id !== id));
    setStats(prev => prev ? { ...prev, pending_itineraries: prev.pending_itineraries - 1, total_pending: prev.total_pending - 1 } : prev);
  };

  const handleRoleToggle = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    await updateUserRole(userId, newRole);
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
  };

  const handleTakeDownAtlas = async (id) => {
    if (!window.confirm('Take down this atlas file? It will be removed from public view.')) return;
    try {
      await unpublishAtlasFile(id);
      setAllAtlas(prev => prev.map(f => f.id === id ? { ...f, moderation_status: 'rejected', published_at: null } : f));
    } catch (err) {
      alert('Failed to take down: ' + err.message);
    }
  };

  const handleTakeDownItin = async (id) => {
    if (!window.confirm('Take down this itinerary? It will be unpublished from the community.')) return;
    try {
      await unpublishItinerary(id);
      setAllItineraries(prev => prev.filter(i => i.id !== id));
    } catch (err) {
      alert('Failed to take down: ' + err.message);
    }
  };

  if (authLoading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral-400" />
        </div>
      </PageContainer>
    );
  }

  if (!isAdmin) return null;

  // Combine atlas files + published itineraries into a unified moderation view.
  // For atlas files, a row with no `published_at` is effectively a draft even if
  // moderation_status defaults to 'approved' — surface it as "draft" in the UI so
  // the admin can find their in-progress work.
  const effectiveAtlasStatus = (f) => {
    if (f.moderation_status === 'rejected') return 'rejected';
    if (f.moderation_status === 'pending') return 'pending';
    return f.published_at ? 'approved' : 'draft';
  };
  const combinedContent = [
    ...allAtlas.map(f => ({
      id: f.id,
      kind: 'atlas',
      title: f.title,
      destination: f.destination,
      author: f.author || '—',
      moderation_status: effectiveAtlasStatus(f),
      updated_at: f.updated_at,
    })),
    ...allItineraries.map(i => ({
      id: i.id,
      kind: 'itinerary',
      title: i.title,
      destination: i.destination,
      author: '—',
      moderation_status: i.moderation_status || 'approved',
      updated_at: i.updated_at,
    })),
  ];

  const filteredContent = combinedContent.filter(item => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q || item.title.toLowerCase().includes(q) || item.destination?.toLowerCase().includes(q);
    const matchesStatus = statusFilter === 'all' || item.moderation_status === statusFilter;
    const matchesType = typeFilter === 'all' || item.kind === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  }).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));

  const tabs = [
    { id: 'pending', label: 'Pending Reviews', icon: Clock, count: stats?.total_pending },
    { id: 'content', label: 'All Content', icon: FileText },
    { id: 'users', label: 'Users', icon: Users },
  ];

  return (
    <PageContainer>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-charcoal-500 flex items-center justify-center">
              <Shield size={22} className="text-naples-400" />
            </div>
            <div>
              <h1 className="text-3xl text-charcoal-500">Admin Dashboard</h1>
              <p className="text-platinum-600">Manage content, users, and moderation</p>
            </div>
          </div>
          <Link to="/atlas/new?type=curated">
            <Button className="gap-2">
              <Award size={18} />
              Create Curated Story
            </Button>
          </Link>
        </div>

        {/* Stats Row */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
            <StatCard icon={AlertTriangle} label="Pending Review" value={stats.total_pending} color="bg-amber-100 text-amber-700" />
            <StatCard icon={FileText} label="Atlas Files" value={stats.total_atlas_files} color="bg-columbia-100 text-columbia-700" />
            <StatCard icon={Map} label="Itineraries" value={stats.total_itineraries} color="bg-coral-50 text-coral-600" />
            <StatCard icon={Users} label="Users" value={stats.total_users} color="bg-naples-100 text-naples-700" />
          </div>
        )}

        {/* Dashboard-wide load error (stats + pending lists) */}
        {statsError && !stats && (
          <Card className="mb-10">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <AlertTriangle size={22} className="text-amber-500 flex-shrink-0" />
                <div>
                  <p className="text-charcoal-500 font-medium">Server is waking up</p>
                  <p className="text-sm text-platinum-600 mt-0.5">The backend server sleeps after inactivity (free tier). Click Retry to wake it up — it may take up to a minute.</p>
                </div>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => { resetServerReady(); fetchData(); }}
                className="flex-shrink-0"
              >
                Retry
              </Button>
            </div>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-8 bg-platinum-100 rounded-xl p-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-charcoal-500 shadow-sm'
                  : 'text-platinum-600 hover:text-charcoal-400'
              }`}
            >
              <tab.icon size={15} />
              {tab.label}
              {tab.count > 0 && (
                <span className="ml-1 px-2 py-0.5 rounded-full bg-coral-400 text-white text-xs font-semibold">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* TAB: Pending Reviews */}
        {activeTab === 'pending' && (loading ? (
          <Card>
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral-400" />
              <p className="text-sm text-platinum-500">Waking up server... this can take up to a minute</p>
            </div>
          </Card>
        ) : statsError ? (
          <Card>
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <AlertTriangle size={32} className="text-amber-400" />
              <p className="text-sm text-charcoal-500 font-medium">{statsError}</p>
              <Button
                variant="primary"
                size="sm"
                onClick={() => { resetServerReady(); fetchData(); }}
                className="mt-2"
              >
                Retry
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-8">
            {/* Pending Atlas Files */}
            <div>
              <h2 className="text-lg font-semibold text-charcoal-500 mb-4 flex items-center gap-2">
                <FileText size={18} className="text-columbia-500" />
                Pending Atlas Files ({pendingAtlas.length})
              </h2>
              {pendingAtlas.length === 0 ? (
                <Card><p className="text-platinum-600 text-center py-8">No pending atlas files</p></Card>
              ) : (
                <div className="space-y-3">
                  {pendingAtlas.map(file => (
                    <Card key={file.id} className="flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-charcoal-500 truncate">{file.title}</h3>
                          <StatusBadge status="pending" />
                        </div>
                        <div className="flex items-center gap-3 text-sm text-platinum-600">
                          {file.destination && <span>{file.destination}</span>}
                          {file.author && <span>by {file.author}</span>}
                          <span>{new Date(file.updated_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Link to={`/atlas/${file.id}`}>
                          <Button variant="ghost" size="sm" className="gap-1.5">
                            <Eye size={14} /> Preview
                          </Button>
                        </Link>
                        <Button size="sm" onClick={() => handleApproveAtlas(file.id)} className="gap-1.5 bg-green-500 hover:bg-green-600 text-white">
                          <CheckCircle size={14} /> Approve
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleRejectAtlas(file.id)} className="gap-1.5 text-red-600 hover:bg-red-50">
                          <XCircle size={14} /> Reject
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Pending Itineraries */}
            <div>
              <h2 className="text-lg font-semibold text-charcoal-500 mb-4 flex items-center gap-2">
                <Map size={18} className="text-coral-400" />
                Pending Itineraries ({pendingItins.length})
              </h2>
              {pendingItins.length === 0 ? (
                <Card><p className="text-platinum-600 text-center py-8">No pending itineraries</p></Card>
              ) : (
                <div className="space-y-3">
                  {pendingItins.map(itin => (
                    <Card key={itin.id} className="flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-charcoal-500 truncate">{itin.title}</h3>
                          <StatusBadge status="pending" />
                        </div>
                        <div className="flex items-center gap-3 text-sm text-platinum-600">
                          <span>{itin.destination}</span>
                          <span>{itin.trip_length}d</span>
                          <span>{new Date(itin.updated_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Link to={`/itinerary/${itin.id}`}>
                          <Button variant="ghost" size="sm" className="gap-1.5">
                            <Eye size={14} /> Preview
                          </Button>
                        </Link>
                        <Button size="sm" onClick={() => handleApproveItin(itin.id)} className="gap-1.5 bg-green-500 hover:bg-green-600 text-white">
                          <CheckCircle size={14} /> Approve
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleRejectItin(itin.id)} className="gap-1.5 text-red-600 hover:bg-red-50">
                          <XCircle size={14} /> Reject
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {/* TAB: All Content */}
        {activeTab === 'content' && (
          <div>
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <div className="relative flex-1 min-w-[220px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-platinum-500" size={16} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search by title or destination..."
                  className="w-full pl-9 pr-4 py-2.5 border border-platinum-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-coral-200 focus:border-coral-300 text-sm transition-all"
                />
              </div>
              <select
                value={typeFilter}
                onChange={e => setTypeFilter(e.target.value)}
                className="px-4 py-2.5 border border-platinum-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-coral-200 focus:border-coral-300"
              >
                <option value="all">All Types</option>
                <option value="atlas">Atlas Files</option>
                <option value="itinerary">Itineraries</option>
              </select>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="px-4 py-2.5 border border-platinum-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-coral-200 focus:border-coral-300"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
              </select>
              <button
                onClick={fetchAllContent}
                className="px-4 py-2.5 text-sm text-platinum-600 hover:text-charcoal-500 transition-colors"
              >
                Refresh
              </button>
            </div>

            {contentLoading ? (
              <Card><div className="flex items-center justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral-400" /></div></Card>
            ) : contentError ? (
              <Card>
                <div className="text-center py-8">
                  <AlertTriangle size={32} className="mx-auto text-red-500 mb-2" />
                  <p className="text-red-600 font-medium">Failed to load content</p>
                  <p className="text-sm text-platinum-600 mt-1">{contentError}</p>
                  <button
                    onClick={fetchAllContent}
                    className="mt-4 text-sm text-coral-500 hover:text-coral-600 font-medium"
                  >
                    Retry
                  </button>
                </div>
              </Card>
            ) : filteredContent.length === 0 ? (
              <Card><p className="text-platinum-600 text-center py-8">No content found</p></Card>
            ) : (
              <div className="space-y-2">
                {/* Table header */}
                <div className="grid grid-cols-12 gap-4 px-5 py-2 text-xs font-medium text-platinum-500 uppercase tracking-wider">
                  <div className="col-span-3">Title</div>
                  <div className="col-span-1">Type</div>
                  <div className="col-span-2">Destination</div>
                  <div className="col-span-2">Author</div>
                  <div className="col-span-1">Status</div>
                  <div className="col-span-1">Updated</div>
                  <div className="col-span-2 text-right">Actions</div>
                </div>
                {filteredContent.map(item => {
                  const isEditableDraft = item.kind === 'atlas' && item.moderation_status === 'draft';
                  const previewHref = isEditableDraft
                    ? `/atlas/edit/${item.id}`
                    : item.kind === 'atlas' ? `/atlas/${item.id}` : `/itinerary/${item.id}`;
                  const canTakeDown = item.moderation_status === 'approved' || item.moderation_status === 'pending';
                  return (
                    <Card key={`${item.kind}-${item.id}`} className="!p-0">
                      <div className="grid grid-cols-12 gap-4 px-5 py-3.5 items-center hover:bg-platinum-50 transition-colors">
                        <div className="col-span-3 font-medium text-charcoal-500 truncate text-sm">{item.title}</div>
                        <div className="col-span-1">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            item.kind === 'atlas' ? 'bg-columbia-100 text-columbia-700' : 'bg-coral-50 text-coral-600'
                          }`}>
                            {item.kind === 'atlas' ? <FileText size={10} /> : <Map size={10} />}
                            {item.kind === 'atlas' ? 'Atlas' : 'Trip'}
                          </span>
                        </div>
                        <div className="col-span-2 text-sm text-platinum-600 truncate">{item.destination}</div>
                        <div className="col-span-2 text-sm text-platinum-600 truncate">{item.author}</div>
                        <div className="col-span-1"><StatusBadge status={item.moderation_status} /></div>
                        <div className="col-span-1 text-sm text-platinum-500">{new Date(item.updated_at).toLocaleDateString()}</div>
                        <div className="col-span-2 flex items-center justify-end gap-1.5">
                          <Link to={previewHref}>
                            <Button variant="ghost" size="sm" className="gap-1">
                              <Eye size={13} /> View
                            </Button>
                          </Link>
                          {canTakeDown && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => item.kind === 'atlas' ? handleTakeDownAtlas(item.id) : handleTakeDownItin(item.id)}
                              className="gap-1 text-red-600 hover:bg-red-50"
                            >
                              <XCircle size={13} /> Take Down
                            </Button>
                          )}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* TAB: Users */}
        {activeTab === 'users' && (
          <div>
            {usersLoading ? (
              <Card><div className="flex items-center justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral-400" /></div></Card>
            ) : usersError ? (
              <Card>
                <div className="text-center py-8">
                  <AlertTriangle size={32} className="mx-auto text-red-500 mb-2" />
                  <p className="text-red-600 font-medium">Failed to load users</p>
                  <p className="text-sm text-platinum-600 mt-1">{usersError}</p>
                  <button
                    onClick={fetchUsers}
                    className="mt-4 text-sm text-coral-500 hover:text-coral-600 font-medium"
                  >
                    Retry
                  </button>
                </div>
              </Card>
            ) : users.length === 0 ? (
              <Card><p className="text-platinum-600 text-center py-8">No users found</p></Card>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-4 px-5 py-2 text-xs font-medium text-platinum-500 uppercase tracking-wider">
                  <div className="col-span-3">Name</div>
                  <div className="col-span-3">Username</div>
                  <div className="col-span-1">Role</div>
                  <div className="col-span-1">Atlas</div>
                  <div className="col-span-1">Trips</div>
                  <div className="col-span-2">Joined</div>
                  <div className="col-span-1">Actions</div>
                </div>
                {users.map(user => (
                  <Card key={user.id} className="!p-0">
                    <div className="grid grid-cols-12 gap-4 px-5 py-3.5 items-center">
                      <div className="col-span-3 font-medium text-charcoal-500 truncate text-sm">{user.full_name || '—'}</div>
                      <div className="col-span-3 text-sm text-platinum-600 truncate">{user.username || '—'}</div>
                      <div className="col-span-1">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          user.role === 'admin' ? 'bg-naples-100 text-naples-700' : 'bg-platinum-100 text-platinum-600'
                        }`}>
                          {user.role === 'admin' && <Shield size={9} />}
                          {user.role}
                        </span>
                      </div>
                      <div className="col-span-1 text-sm text-platinum-600">{user.atlas_count}</div>
                      <div className="col-span-1 text-sm text-platinum-600">{user.itinerary_count}</div>
                      <div className="col-span-2 text-sm text-platinum-500">{new Date(user.created_at).toLocaleDateString()}</div>
                      <div className="col-span-1">
                        <button
                          onClick={() => handleRoleToggle(user.id, user.role)}
                          className="text-xs font-medium text-coral-500 hover:text-coral-600 transition-colors"
                        >
                          {user.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
                        </button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </PageContainer>
  );
}

export default AdminDashboardPage;
